import { useState } from "react";
import {
  Alert,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import type { InquiryListParams } from "@bw/shared/api";
import { useAuth, useInquiryInbox } from "@bw/shared/hooks";
import { visuallyHidden } from "../../../common/visuallyHidden";

type Status = NonNullable<InquiryListParams["lifecycle"]>;
export function InquiryInboxPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>("open");
  const query = useInquiryInbox(user?.uid, {
    scope: "mine",
    lifecycle: status,
    limit: 30,
  });
  const rows = [
    ...new Map(
      (query.data?.pages.flatMap((page) => page.items) ?? []).map((item) => [
        item.conversation_id,
        item,
      ]),
    ).values(),
  ];
  return (
    <Stack useFlexGap spacing={2} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Typography component="h1" sx={visuallyHidden}>
        Service inquiries
      </Typography>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Button component={RouterLink} to="/community/messages" size="small" variant="outlined">
          Back to messages
        </Button>
        <TextField
          select
          label="Status"
          size="small"
          value={status}
          onChange={(event) => setStatus(event.target.value as Status)}
          sx={{ width: 180 }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
          <MenuItem value="all">All history</MenuItem>
        </TextField>
      </Stack>
      {!user ? (
        <Typography>Sign in to view inquiries.</Typography>
      ) : query.isError ? (
        <Alert
          severity="warning"
          action={
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your inquiries. Your access may have changed.
        </Alert>
      ) : query.isPending ? (
        <Typography role="status">Loading inquiries…</Typography>
      ) : (
        <>
          {rows.length ? (
            <List disablePadding>
              {rows.map((item) => (
                <ListItemButton
                  key={item.conversation_id}
                  component={RouterLink}
                  to={`/community/inquiries/${encodeURIComponent(item.conversation_id)}`}
                  divider
                >
                  <ListItemText
                    primary={item.topic}
                    secondary={
                      item.target_name ?? item.facility_name ?? "Inquiry"
                    }
                  />
                  {item.unread_count > 0 ? (
                    <Chip size="small" label={`${item.unread_count} unread`} />
                  ) : null}
                </ListItemButton>
              ))}
            </List>
          ) : !query.hasNextPage ? (
            <Typography color="text.secondary">
              No inquiries match this status.
            </Typography>
          ) : null}
          {query.hasNextPage ? (
            <Button
              size="small"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
              sx={{ alignSelf: "flex-start" }}
            >
              {query.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </>
      )}
    </Stack>
  );
}
